import { PaymentsConfig, Customer, Subscription } from '../../shared/types';
import { ERROR_MESSAGES } from '../../shared/constants';

export const createSubscriptionSetup = async (params: {
  priceId: string;
  customerId?: string;
  customerEmail?: string;
  metadata?: Record<string, string>;
  trialPeriodDays?: number;
}): Promise<
  | {
      setupIntentClientSecret: string;
      customerId: string;
      customerEphemeralKeySecret: string;
    }
  | { error: string }
> => {
  try {
    const response = await fetch('/api/payments/create-subscription-setup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return {
        error: errorData.error || 'Failed to create subscription setup',
      };
    }

    const data = await response.json();
    return {
      setupIntentClientSecret: data.setupIntentClientSecret,
      customerId: data.customerId,
      customerEphemeralKeySecret: data.customerEphemeralKeySecret,
    };
  } catch (error) {
    return {
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create subscription setup',
    };
  }
};

export const createPaymentIntent = async (params: {
  amount: number;
  currency: string;
  customerId?: string;
  description?: string;
  metadata?: Record<string, string>;
}): Promise<{ clientSecret: string } | { error: string }> => {
  try {
    const response = await fetch('/api/payments/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return { error: errorData.error || 'Failed to create payment intent' };
    }

    const data = await response.json();
    return { clientSecret: data.client_secret };
  } catch (error) {
    return {
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create payment intent',
    };
  }
};

export const validatePaymentsConfig = (
  config: PaymentsConfig
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!config.publishableKey) {
    errors.push('publishableKey is required');
  }

  if (!config.provider) {
    errors.push('provider is required');
  }

  if (config.provider !== 'stripe') {
    errors.push('Only stripe provider is currently supported');
  }

  if (!config.environment) {
    errors.push('environment is required');
  }

  if (
    config.environment &&
    !['development', 'production'].includes(config.environment)
  ) {
    errors.push('environment must be either "development" or "production"');
  }

  if (config.publishableKey && !config.publishableKey.startsWith('pk_')) {
    errors.push('publishableKey must start with "pk_"');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const formatStripeError = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null) {
    if (
      'message' in error &&
      typeof (error as { message: unknown }).message === 'string'
    ) {
      return (error as { message: string }).message;
    }

    if (
      'code' in error &&
      typeof (error as { code: unknown }).code === 'string'
    ) {
      const code = (error as { code: string }).code;
      const errorMessages: Record<string, string> = {
        Canceled: 'Payment was cancelled',
        Failed: 'Payment failed',
        card_declined: 'Your card was declined.',
        expired_card: 'Your card has expired.',
        incorrect_cvc: "Your card's security code is incorrect.",
        processing_error: 'An error occurred while processing your card.',
        incorrect_number: 'Your card number is incorrect.',
      };
      return errorMessages[code] || `Payment failed: ${code}`;
    }
  }

  return 'An unexpected error occurred.';
};

export const createCustomer = async (params: {
  email: string;
  name?: string;
  phone?: string;
  metadata?: Record<string, string>;
}): Promise<{ customer: Customer } | { error: string }> => {
  try {
    const response = await fetch('/api/payments/customer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return { error: errorData.error || 'Failed to create customer' };
    }

    const customer = await response.json();
    return { customer };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : 'Failed to create customer',
    };
  }
};

export const fetchSubscription = async (
  subscriptionId: string
): Promise<{ subscription: Subscription } | { error: string }> => {
  try {
    const response = await fetch(
      `/api/payments/subscription/${subscriptionId}`
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return {
        error: errorData.error || ERROR_MESSAGES.SUBSCRIPTION_NOT_FOUND,
      };
    }

    const subscription = await response.json();
    return { subscription };
  } catch (error) {
    return {
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.SUBSCRIPTION_NOT_FOUND,
    };
  }
};

export const fetchCustomerSubscriptions = async (
  customerId: string
): Promise<{ subscriptions: Subscription[] } | { error: string }> => {
  try {
    const response = await fetch(
      `/api/payments/customer/${customerId}/subscriptions`
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return { error: errorData.error || ERROR_MESSAGES.CUSTOMER_NOT_FOUND };
    }

    const data = await response.json();
    return { subscriptions: data.subscriptions || [] };
  } catch (error) {
    return {
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.CUSTOMER_NOT_FOUND,
    };
  }
};

export const cancelSubscription = async (
  subscriptionId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      `/api/payments/subscription/${subscriptionId}/cancel`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return {
        success: false,
        error: errorData.error || 'Failed to cancel subscription',
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to cancel subscription',
    };
  }
};
